import _ from 'lodash';
import moment from 'moment';

import cashflowViewConsts from '../../constants/cashflow-view';

const EXCLUDED_REASONS = {
  CUSTOMER_RULE: 'customer_rule',
};

const SHORT_LABEL = {
  'תחבורה ציבורית': 'תחב"צ',
};

function normalizeCashflow(cashflow) {
  return {
    [cashflowViewConsts.cashflowTypes.income]: _getIncomeSubtree(cashflow),
    [cashflowViewConsts.cashflowTypes.economy]: _getEconomySubtree(cashflow),
    [cashflowViewConsts.cashflowTypes.variableExpense]: _getVariableExpenseSubtree(cashflow),
    [cashflowViewConsts.cashflowTypes.fixedExpense]: _getFixedExpenseSubtree(cashflow),
    [cashflowViewConsts.cashflowTypes.recurring]: _getRecurringSubtree(cashflow),
    [cashflowViewConsts.cashflowTypes.trackingCategory]: _getTrackingCategoriesSubtree(cashflow),
    [cashflowViewConsts.cashflowTypes.excluded]: _getExcludedTransactions(cashflow),
  };
}

function _getIncomeSubtree(cashflow) {
  const { fixedTree } = cashflow;
  return {
    upToNowAmount: cashflow.incomeEnvelopesActualSum + cashflow.variableIncomeSum,
    recommendedOrExpectedAmount: cashflow.incomeEnvelopesBalanceSum + cashflow.variableIncomeBalancedAmount,
    fixedIncome: {
      categories: _.map(fixedTree.incomeCategories, _getFixCategory),
    },
    variableIncome: {
      categories: [_getVariableDetails(cashflow.variableIncomeSum, cashflow.variableIncomeBalancedAmount, cashflow.variableIncomeTransactions)],
    },
  };
}

function _getExcludedTransactions(cashflow) {
  const allExcluded = excludedByCustomerRule(cashflow.excluded);
  const income = _.chain(allExcluded).filter(t => _.isNumber(t.incomeAmount)).sortBy(t => t.transactionDate).value();
  const expense = _.chain(allExcluded).filter(t => !_.isNumber(t.incomeAmount)).sortBy(t => t.transactionDate).value();
  return {
    excludedIncome: {
      categories: [{
        label: 'הכנסות',
        actualsSum: _.sumBy(income, t => t.incomeAmount),
        transactions: income,
      }],
    },
    excludedExpense: {
      categories: [{
        label: 'הוצאות',
        actualsSum: _.sumBy(expense, t => t.billingAmount),
        transactions: expense,
      }],
    },
  };
}

function excludedByCustomerRule(transactions) {
  return _.filter(transactions, t => t.excludeReason === EXCLUDED_REASONS.CUSTOMER_RULE);
}

function _getTrackingCategoriesSubtree(cashflow) {
  const trackingCategoriesEnvelopes = cashflow.getEnvelopesByType(cashflowViewConsts.cashflowTypes.trackingCategory);
  return _.map(trackingCategoriesEnvelopes, envelope => {
    return {
      envelopeId: envelope.id,
      categoryName: envelope.details.trackingCategory.name,
      categoryId: envelope.details.trackingCategory._id,
      upToNowAmount: envelope.actualsAmount,
      recommendedOrExpectedAmount: envelope.balancedAmount,
      categories: [_trackingCategoryEnvelopeToCategory(envelope)],
      icon: envelope.details.trackingCategory.icon,
    };
  });
}


function _getRecurringSubtree(cashflow) {
  const recurringEnvelopes = cashflow.getEnvelopesByType(cashflowViewConsts.cashflowTypes.recurring);
  return _.map(recurringEnvelopes, envelope => {
    return {
      envelopeId: envelope.id,
      expenseCategory: envelope.details.expenseCategory,
      upToNowAmount: envelope.actualsAmount,
      recommendedOrExpectedAmount: envelope.balancedAmount,
      categories: [_recurringEnvelopeToCategory(envelope)],
    };
  });
}

function _getEconomySubtree(cashflow) {
  const economyEnvelopes = cashflow.getEnvelopesByType(cashflowViewConsts.cashflowTypes.economy);
  return {
    upToNowAmount: _.sumBy(economyEnvelopes, envelope => envelope.actualsAmount),
    recommendedOrExpectedAmount: _.sumBy(economyEnvelopes, envelope => envelope.balancedAmount),
    categories: _.chain(economyEnvelopes)
      .sortBy(envelope => envelope.details.weeklyIndex)
      .map(_economyEnvelopeToCategory)
      .value(),
  };
}

function _getFixedExpenseSubtree(cashflow) {
  const { fixedTree } = cashflow;
  return {
    upToNowAmount: fixedTree.fixedExpenseEnvelopesActualSum,
    recommendedOrExpectedAmount: fixedTree.fixedExpenseEnvelopesBalanceSum,
    categories: _getFixedCategories(fixedTree),
  };
}

function _getFixedCategories(fixedTree) {
  const allExpenseCategoriesOrdered = [
    ...cashflowViewConsts.TRANSFERABLE_FIXED_EXPENSE_CATEGORIES,
    ...cashflowViewConsts.NON_TRANSFERABLE_FIXED_EXPENSE_CATEGORIES,
  ];
  return _.chain(fixedTree.expenseCategories)
    .map(_getFixCategory)
    .sortBy(_.partialRight(_getFixedOrderIndex, allExpenseCategoriesOrdered))
    .thru(_markFirstNonTransferableCategory)
    .value();
}

function _getVariableExpenseSubtree(cashflow) {
  const currentMonthVariableExpenses = cashflow.weeklyVariables.toJson();
  return {
    upToNowAmount: cashflow.variableActualSum,
    recommendedOrExpectedAmount: _.max([cashflow.variableBalancedAmount, 0]),
    monthly: {
      realExpenses: {
        categories: [_getVariableDetails(
          cashflow.variableActualSum,
          cashflow.variableBalancedAmount,
          cashflow.variableTransactions,
        )],
      },
    },
    weekly: {
      realExpenses: {
        categories: _.map(currentMonthVariableExpenses.weeks, week => {
          const label = `שבוע ${week.weekIndex + 1}`;
          const { sum: actualsSum, transactions, remaining: recommendedAmount } = week;
          const weekTimeFrame = _getEnvelopeWeekTimeFrame(week, moment());
          const disabled = _isFutureWeek(weekTimeFrame) && week.transactions.length === 0;
          const sortedTransactions = _.sortBy(transactions, t => t.transactionDate);
          return {
            ...week,
            label,
            actualsSum,
            transactions: sortedTransactions,
            recommendedAmount,
            disabled,
            initiallyExpanded: false,
            timeFrameDetails: {
              timeFrame: cashflowViewConsts.envelopeTimeFrames.weekly,
              isCurrentWeek: weekTimeFrame === cashflowViewConsts.weekTimeFrames.current,
              weeklyIndex: week.weekIndex,
            },
          };
        }),
      },
    },
  };
}

function _isFutureWeek(weekTimeFrame) {
  return weekTimeFrame === cashflowViewConsts.weekTimeFrames.future;
}

function _getFixCategory(fixCategory) {
  return {
    label: fixCategory.expense,
    actualsSum: fixCategory.actualsAmount,
    recommendedAmount: fixCategory.balancedAmount,
    transactions: toPredictedTransactions(fixCategory.envelopes),
    initiallyExpanded: false,
  };
}

function _getFixedOrderIndex(fixCategory, allExpenseCategoriesOrdered) {
  return _.findIndex(allExpenseCategoriesOrdered, c => c === fixCategory.label);
}

function _markFirstNonTransferableCategory(orderedCategories) {
  const index = _.findIndex(orderedCategories, c => _.includes(cashflowViewConsts.NON_TRANSFERABLE_FIXED_EXPENSE_CATEGORIES, c.label));
  if (index > -1) {
    orderedCategories[index].showSeparateLine = true; // eslint-disable-line
  }
  return orderedCategories;
}

function _economyEnvelopeToCategory(envelope) {
  const { weeklyIndex } = envelope.details;
  const label = `שבוע ${weeklyIndex + 1}`;
  const weekTimeFrame = _getEnvelopeWeekTimeFrame(envelope.details, moment());
  return {
    label,
    actualsSum: envelope.actualsAmount,
    recommendedAmount: envelope.balancedAmount,
    transactions: _.sortBy(envelope.actuals, t => t.transactionDate),
    initiallyExpanded: false,
    disabled: weekTimeFrame === cashflowViewConsts.weekTimeFrames.future,
    isEditable: !envelope.balanceDatePassed,
    categoryId: envelope.id,
    envelopeId: envelope.id,
    timeFrameDetails: {
      timeFrame: cashflowViewConsts.envelopeTimeFrames.weekly,
      weeklyIndex,
      startDate: envelope.details.startDate,
      endDate: moment(envelope.details.endDate).subtract(1, 'days').format('YYYY-MM-DD'),
      isCurrentWeek: weekTimeFrame === cashflowViewConsts.weekTimeFrames.current,
    },
  };
}

function _trackingCategoryEnvelopeToCategory(envelope) {
  const { trackingCategory } = envelope.details;
  return {
    label: trackingCategory.name,
    shortLabel: SHORT_LABEL[trackingCategory.name],
    actualsSum: envelope.actualsAmount,
    recommendedAmount: envelope.balancedAmount,
    transactions: _.sortBy(envelope.actuals, t => t.transactionDate),
    initiallyExpanded: true,
    disabled: false,
    isEditable: !envelope.balanceDatePassed,
    categoryId: envelope.details.trackingCategory._id,
    envelopeId: envelope.id,
    timeFrameDetails: {
      timeFrame: cashflowViewConsts.envelopeTimeFrames.monthly,
    },
  };
}


function _recurringEnvelopeToCategory(envelope) {
  const { expenseCategory } = envelope.details;
  return {
    label: expenseCategory,
    shortLabel: SHORT_LABEL[expenseCategory],
    actualsSum: envelope.actualsAmount,
    recommendedAmount: envelope.balancedAmount,
    transactions: _.sortBy(envelope.actuals, t => t.transactionDate),
    initiallyExpanded: true,
    disabled: false,
    isEditable: !envelope.balanceDatePassed,
    categoryId: envelope.id,
    envelopeId: envelope.id,
    timeFrameDetails: {
      timeFrame: cashflowViewConsts.envelopeTimeFrames.monthly,
    },
  };
}

function _getVariableDetails(actualsSum, recommendedAmount, transactions) {
  return {
    label: 'משתנות',
    actualsSum,
    recommendedAmount,
    transactions: _.sortBy(transactions, t => t.transactionDate),
    initiallyExpanded: true,
    disabled: false,
  };
}

function toPredictedTransactions(envelopes) {
  return _.map(envelopes, envelope => {
    return {
      name: envelope.details.businessName,
      expense: envelope.details.expense,
      predictedAmount: envelope.balancedAmount,
      actual: envelope.actuals[0],
      previousTransactionId: envelope.details.previousTransactionId,
      predictedTransactionDate: envelope.details.transactionDate,
      envelopeId: envelope.id,
      sequenceId: envelope.details.sequenceId,
      isIncome: _isIncome(envelope.originalAmount),
    };
  });
}

function _isIncome(amount) {
  return amount < 0;
}

function _getEnvelopeWeekTimeFrame(weekDetails, now) {
  const { endDate, startDate } = weekDetails;
  if (moment(endDate) <= now) {
    return cashflowViewConsts.weekTimeFrames.past;
  }
  if (moment(startDate) <= now && now < moment(endDate)) {
    return cashflowViewConsts.weekTimeFrames.current;
  }
  return cashflowViewConsts.weekTimeFrames.future;
}

export default {
  normalizeCashflow,
  excludedByCustomerRule,
};



// WEBPACK FOOTER //
// ./src/store/utilities/cashflow-normalizer.js